import { Grow } from '@mui/material';
import { timeDay } from 'd3';
import _ from 'lodash';
import React, { useContext } from 'react';
import { mdiMessageTextOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { MessagingContext } from '../../../services';
import { formatDate } from '../../../services/primitives/date-utils';
import { Tooltip, Variables } from '../../base';
import MarkdownWrapper from '../../base/markdown-wrapper/markdown-wrapper';
import { AttachmentButton } from '../attachment-button';
import { MessagingBadge, MessagingPill } from '../messaging-badge';
import { UserAvatarLogo, UserAvatarLogoContainer } from '../message-list.styles';
import enterLogo from '../icons/enter-services-icon.svg';
import { authorNickname, IMessagingComment, isHtmlContent, sanitizeHtml } from '../util';
import * as S from './chat-history.styles';

const getInitials = (name: string): string => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const formatUpdated = (isoString: string | undefined): string => {
    if (!isoString) return '';
    try {
        const d = new Date(isoString);
        return formatDate(d, 'MMM d, yyyy h:mm a');
    } catch {
        return '';
    }
};

interface IChatHistory {
    hideNote?: boolean;
}

export const ChatHistory: React.FunctionComponent<IChatHistory> = (props: IChatHistory): React.ReactElement => {
    const { hideNote } = props;
    const context = useContext(MessagingContext);
    const { comments, documents, message } = context;

    const mdyFormat = (d: Date): string => formatDate(d, 'MMM d, yyyy');
    const timeFormat = (d: Date): string => formatDate(d, 'HH:mm');
    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());

    const isNewDay = (currDate?: Date, nextDate?: Date): boolean => {
        let isSameDay = false;
        if (currDate && nextDate) {
            isSameDay = timeDay.count(currDate, nextDate) <= 0;
        }
        return !isSameDay;
    };

    // NB: skip first comment, as it is shown in the chat body
    const renderAttachments = (comment: IMessagingComment): React.ReactElement | React.ReactElement[] | undefined => {
        const commentDocIds = comment.documents || [];
        const commentDocs = _.filter(documents, document => _.includes(commentDocIds, document.id));

        // Show loading indicator when documents are being fetched
        if (commentDocIds.length > 0 && commentDocs.length === 0) {
            return <S.LoadingText>Loading documents...</S.LoadingText>;
        }

        return _.times(_.size(commentDocs), (i: number): React.ReactElement => {
            const doc = commentDocs[i];
            const onDownload = async (): Promise<void> => {
                if (doc.id) {
                    await (context.onDocumentDownload && context.onDocumentDownload(doc.id));
                }
            };

            const onActualDownload = async (): Promise<string | void> => {
                if (doc && doc.id && context.onDocumentActualDownload) {
                    return await context.onDocumentActualDownload(doc.id);
                }
            };

            const onAssign = async (): Promise<void> => {
                await (context.onDocumentAssign && context.onDocumentAssign(doc));
            };

            const onEdit = async (): Promise<void> => {
                await (context.onDocumentEdit && context.onDocumentEdit(doc));
            };

            const isSupportApp = context.account?.supportGrant === true;
            const canAssign =
                context.message?.links?.claims?.length &&
                context.message?.links?.claims?.length > 0 &&
                !doc.claimId &&
                !!context.onDocumentAssign;

            return (
                <S.HistoryEntryAttachment key={`attachment-${i}`}>
                    <AttachmentButton
                        key={`attachment-${i}`}
                        filename={doc.filename as string}
                        filesize={doc.filesize}
                        thumbnail={doc.thumbnail}
                        documentId={doc.id}
                        onDownload={onDownload}
                        onActualDownload={
                            context.onDocumentActualDownload ? onActualDownload : undefined
                        }
                        onAssign={canAssign ? onAssign : undefined}
                        onEdit={context.onDocumentEdit ? onEdit : undefined}
                        isSupportApp={isSupportApp}
                    />
                </S.HistoryEntryAttachment>
            );
        });
    };

    const renderHeader = (date: Date, i: number): React.ReactElement => (
        <S.HistoryHeader key={`header-${date}${i}`}>
            <S.HistoryHeaderLine />
            <S.HistoryHeaderLabel>{mdyFormat(date)}</S.HistoryHeaderLabel>
            <S.HistoryHeaderLine />
        </S.HistoryHeader>
    );

    const renderComment = (comment: IMessagingComment, i: number): React.ReactElement => {
        const hasNote = _.get(comment, 'note', false);
        const isHtml = isHtmlContent(comment.body);
        const authorName = authorNickname(comment);
        const authorInitials = getInitials(authorName);
        const commentDate = parseDate(comment.created);
        const fullTimestamp = formatDate(commentDate, 'MMM d, yyyy h:mm a');

        const authorAvatar =
            authorName === 'Enter, Inc.' ? (
                <Tooltip direction="top" title={<span>{authorName}</span>}>
                    <UserAvatarLogoContainer>
                        <UserAvatarLogo src={enterLogo} alt={authorName} />
                    </UserAvatarLogoContainer>
                </Tooltip>
            ) : (
                <MessagingBadge title={authorName}>{authorInitials || '?'}</MessagingBadge>
            );

        return (
            <Grow in={true} key={comment.id}>
                <S.HistoryEntry note={hasNote} aiNote={authorName === 'Enter Support'}>
                    <S.HistoryEntryHeader>
                        {authorAvatar}
                        <S.HistoryEntryName>{authorName}</S.HistoryEntryName>
                        <S.HistoryEntryTime>{fullTimestamp}</S.HistoryEntryTime>
                        {authorName === 'Enter Support' && <MessagingPill>AI Message</MessagingPill>}
                        {hasNote && (
                            <S.HistoryNoteIndicator>
                                <MessagingPill note={true}>Internal Note</MessagingPill>
                            </S.HistoryNoteIndicator>
                        )}
                    </S.HistoryEntryHeader>
                    <S.HistoryEntryMessage>
                        {isHtml ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
                        ) : (
                            <MarkdownWrapper text={comment.body || ''} />
                        )}
                    </S.HistoryEntryMessage>
                    {renderAttachments(comment)}
                </S.HistoryEntry>
            </Grow>
        );
    };

    const renderComments = (): React.ReactElement[] => {
        const result: React.ReactElement[] = [];
        let lastDay: Date | undefined;
        const historyComments = _.tail<IMessagingComment>(comments as IMessagingComment[]);
        _.forEach(historyComments, (comment: IMessagingComment | undefined, i: number): void => {
            if (comment) {
                if (hideNote && comment.note) {
                    return;
                }
                const date = parseDate(comment.created);
                if (isNewDay(lastDay, date)) {
                    result.push(renderHeader(date, i));
                }
                result.push(renderComment(comment, i));
                lastDay = date;
            }
        });
        return result;
    };

    const historyComments = _.tail<IMessagingComment>(comments as IMessagingComment[]);
    const commentsCount = hideNote ? historyComments.filter(c => !c.note).length : historyComments.length;

    const updatedBy = (message as any)?.updatedBy || message?.lastAuthor;
    const updatedByNickname = updatedBy ? authorNickname({ author: updatedBy } as any) : '';
    const updatedByInitials = getInitials(updatedByNickname);
    const updatedFormatted = formatUpdated(message?.updated);

    return (
        <S.History>
            <S.CommentsHeader>
                <S.CommentsContainer>
                    <S.CommentsIcon>
                        <Icon path={mdiMessageTextOutline} size={0.6} color={Variables.Colors.n85} />
                    </S.CommentsIcon>
                    <S.CommentsLabel>Comments</S.CommentsLabel>
                    <MessagingBadge>{commentsCount}</MessagingBadge>
                </S.CommentsContainer>
                {message?.updated && (
                    <S.CommentsContainer>
                        <MessagingBadge>{updatedByInitials || '?'}</MessagingBadge>
                        <S.UpdatedByText>Updated: {updatedFormatted}</S.UpdatedByText>
                    </S.CommentsContainer>
                )}
            </S.CommentsHeader>
            <S.HistoryBody>{renderComments()}</S.HistoryBody>
        </S.History>
    );
};
